import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Edit, Trash2, Plus, Search, Filter, MoreHorizontal } from 'lucide-react';
import { useRoles, useDeleteRole } from '../../shared/lib/hooks/use-roles';
import { useAuthStore } from '@/app/store/auth-store';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pagination } from '@/components/ui/pagination';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { RoleDeleteDialog } from '@/components/ui/role-delete-dialog';
import { VirtualizedTable, useVirtualization } from '@/components/ui/virtualized-table';
import { RoleTableSkeleton } from '@/components/ui/table-skeleton';
import { cacheUtils } from '../../shared/lib/query-client';
import { useIsMobile } from '@/hooks/use-mobile';

import type { Role, RoleSearchParams, Column } from '@/shared/types';

interface RoleTableProps {
  onCreateRole?: () => void;
  onEditRole?: (role: Role) => void;
  onViewRole?: (role: Role) => void;
  onDeleteRole?: (role: Role) => void;
}

export const RoleTable: React.FC<RoleTableProps> = ({
  onCreateRole,
  onEditRole,
  onViewRole,
  onDeleteRole,
}) => {
  const { t } = useTranslation();
  const { hasPermission } = useAuthStore();
  const isMobile = useIsMobile();
  
  // Search and filter state
  const [searchParams, setSearchParams] = useState<RoleSearchParams>({
    page: 1,
    size: 10,
    search: '',
    serviceName: '',
    sortBy: 'name',
    sortOrder: 'asc',
  });
  
  // Local state for inputs
  const [searchInput, setSearchInput] = useState('');
  const [serviceFilter, setServiceFilter] = useState<string>('');
  
  // Delete confirmation state
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  
  // Fetch roles data
  const { data: rolesData, isLoading, error, refetch, isFetching } = useRoles(searchParams);

  // Determine if we should use virtualization
  const { shouldVirtualize } = useVirtualization(rolesData?.items?.length || 0, 30);

  // Prefetch role details on hover for better UX
  const handleRoleHover = React.useCallback((role: Role) => {
    cacheUtils.prefetchRole(role.id);
  }, []);
  
  // Delete role mutation
  const deleteRoleMutation = useDeleteRole();
  
  // Permission checks
  const canReadRoles = hasPermission('roles:read');
  const canWriteRoles = hasPermission('roles:write');
  const canDeleteRoles = hasPermission('roles:delete');
  
  // Handle search
  const handleSearch = () => {
    setSearchParams(prev => ({
      ...prev,
      search: searchInput,
      page: 1,
    }));
  };
  
  // Handle service filter
  const handleServiceFilter = (value: string) => {
    setServiceFilter(value);
    setSearchParams(prev => ({
      ...prev,
      serviceName: value === 'all' || value === 'global' ? '' : value,
      page: 1,
    }));
  };
  
  // Handle pagination
  const handlePageChange = (page: number) => {
    setSearchParams(prev => ({ ...prev, page }));
  };
  
  const handlePageSizeChange = (size: number) => {
    setSearchParams(prev => ({ ...prev, size, page: 1 }));
  };
  
  // Handle delete confirmation
  const handleDeleteClick = (role: Role) => {
    setRoleToDelete(role);
  };
  
  const handleDeleteConfirm = async () => {
    if (roleToDelete) {
      try {
        await deleteRoleMutation.mutateAsync(roleToDelete.id);
        setRoleToDelete(null);
        // Call the optional callback if provided
        onDeleteRole?.(roleToDelete);
      } catch (error) {
        // Error is handled by the mutation's onError callback
        console.error('Failed to delete role:', error);
      }
    }
  };
  
  // Get unique service names for filter
  const serviceNames = useMemo(() => {
    if (!rolesData?.items) return [];
    const services = new Set(
      rolesData.items
        .map((role: Role) => role.serviceName)
        .filter(Boolean)
    );
    return Array.from(services);
  }, [rolesData?.items]);

  // Mobile card component for roles
  const RoleCard: React.FC<{ 
    role: Role; 
    onMouseEnter?: () => void;
  }> = ({ role, onMouseEnter }) => (
    <Card className="w-full" onMouseEnter={onMouseEnter}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-3">
            {/* Role name and service */}
            <div className="space-y-1">
              <div className="font-medium text-base">{role.name}</div>
              <div className="flex items-center gap-2">
                {role.serviceName ? (
                  <Badge variant="secondary" className="text-xs">{role.serviceName}</Badge>
                ) : (
                  <span className="text-muted-foreground text-xs">Global</span>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">{t('common.description')}:</div>
              <div className="text-sm text-muted-foreground">
                {role.description || t('roles.noDescription')}
              </div>
            </div>

            {/* Permissions */}
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">{t('roles.permissions')}:</div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  {role.permissions.length} {t('roles.permissions').toLowerCase()}
                </Badge>
                {role.permissions.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewRole?.(role);
                    }}
                    className="h-6 px-2 text-xs"
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    {t('common.view')}
                  </Button>
                )}
              </div>
            </div>

            {/* Created date */}
            <div className="text-xs text-muted-foreground">
              {t('common.createdAt')}: {new Date(role.createdAt).toLocaleDateString()}
            </div>
          </div>

          {/* Actions */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onViewRole?.(role)}>
                <Eye className="mr-2 h-4 w-4" />
                {t('roles.viewRole')}
              </DropdownMenuItem>
              {canWriteRoles && (
                <DropdownMenuItem onClick={() => onEditRole?.(role)}>
                  <Edit className="mr-2 h-4 w-4" />
                  {t('common.edit')}
                </DropdownMenuItem>
              )}
              {canDeleteRoles && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => handleDeleteClick(role)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t('common.delete')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
  
  // Table columns
  const columns: Column<Role>[] = useMemo(() => [
    {
      key: 'name',
      title: t('common.name'),
      sortable: true,
      render: (_, role) => (
        <div className="font-medium">
          {role.name}
        </div>
      ),
    },
    {
      key: 'description',
      title: t('common.description'),
      render: (_, role) => (
        <div className="text-muted-foreground max-w-xs truncate">
          {role.description || t('roles.noDescription')}
        </div>
      ),
    },
    {
      key: 'serviceName',
      title: t('roles.serviceName'),
      render: (_, role) => (
        role.serviceName ? (
          <Badge variant="secondary">{role.serviceName}</Badge>
        ) : (
          <span className="text-muted-foreground text-sm">Global</span>
        )
      ),
    },
    {
      key: 'permissions',
      title: t('roles.permissions'),
      render: (_, role) => (
        <div className="flex items-center gap-2">
          <Badge variant="outline">
            {role.permissions.length} {t('roles.permissions').toLowerCase()}
          </Badge>
          {role.permissions.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onViewRole?.(role);
              }}
            >
              <Eye className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
    {
      key: 'createdAt',
      title: t('common.createdAt'),
      render: (_, role) => (
        <span className="text-sm text-muted-foreground">
          {new Date(role.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      title: t('common.actions'),
      align: 'right',
      render: (_, role) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <span className="sr-only">Open menu</span>
              ⋮
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onViewRole?.(role)}>
              <Eye className="mr-2 h-4 w-4" />
              {t('roles.viewRole')}
            </DropdownMenuItem>
            {canWriteRoles && (
              <DropdownMenuItem onClick={() => onEditRole?.(role)}>
                <Edit className="mr-2 h-4 w-4" />
                {t('common.edit')}
              </DropdownMenuItem>
            )}
            {canDeleteRoles && (
              <DropdownMenuItem
                onClick={() => handleDeleteClick(role)}
                className="text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('common.delete')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ], [t, canWriteRoles, canDeleteRoles, onViewRole, onEditRole]);
  
  if (!canReadRoles) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-muted-foreground">
            {t('errors.unauthorized')}
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {t('roles.title')}
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            {t('roles.manageDescription')}
          </p>
        </div>
        {canWriteRoles && (
          <Button onClick={onCreateRole} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            {t('roles.createRole')}
          </Button>
        )}
      </div>
      
      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <Filter className="h-4 w-4 sm:h-5 sm:w-5" />
            {t('common.filters')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="flex gap-2">
                <Input
                  placeholder={t('roles.searchPlaceholder')}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="flex-1 text-base sm:text-sm"
                />
                <Button onClick={handleSearch} variant="outline" size={isMobile ? "default" : "default"}>
                  <Search className="h-4 w-4" />
                  {!isMobile && <span className="ml-2 hidden sm:inline">{t('common.search')}</span>}
                </Button>
              </div>
            </div>
            
            {/* Service Filter */}
            <div className="w-full">
              <Select value={serviceFilter} onValueChange={handleServiceFilter}>
                <SelectTrigger className="text-base sm:text-sm">
                  <SelectValue placeholder={t('roles.filterByService')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  <SelectItem value="global">Global</SelectItem>
                  {serviceNames
                    .filter((s): s is string => Boolean(s))
                    .map((service) => (
                      <SelectItem key={service} value={service}>
                        {service}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Data display - responsive with virtualization */}
      {isLoading && (
        <RoleTableSkeleton 
          rows={searchParams.size || 10} 
          isMobile={isMobile}
        />
      )}

      {/* Fetching indicator for background updates */}
      {isFetching && !isLoading && (
        <div className="absolute top-2 right-2 z-10">
          <div className="h-2 w-2 bg-blue-500 rounded-full animate-pulse" />
        </div>
      )}

      {/* Error state */}
      {!!error && (
        <Card>
          <CardContent className="p-8">
            <div className="text-center space-y-4">
              <h3 className="text-lg font-semibold text-destructive">
                {t('errors.loadFailed')}
              </h3>
              <p className="text-muted-foreground">
                {(error as any).message || t('errors.generic')}
              </p>
              <Button onClick={() => refetch()} variant="outline">
                {t('common.retry')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!isLoading && !error && (!rolesData?.items || rolesData.items.length === 0) && (
        <Card>
          <CardContent className="p-8">
            <div className="text-center space-y-4">
              <h3 className="text-lg font-semibold">
                {searchParams.search 
                  ? t('roles.noRolesFound')
                  : t('roles.noRolesFoundEmpty')}
              </h3>
              <p className="text-muted-foreground">
                {searchParams.search
                  ? t('roles.noRolesFoundSearch')
                  : t('roles.noRolesFoundEmptyDescription')}
              </p>
              {canWriteRoles && !searchParams.search && (
                <Button onClick={() => onCreateRole?.()}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('roles.createRole')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data display - responsive */}
      {!isLoading && !error && rolesData?.items && rolesData.items.length > 0 && (
        <>
          {/* Desktop table view */}
          <div className="hidden md:block">
            <Card>
              <CardContent className="p-0 relative">
                {shouldVirtualize ? (
                  <VirtualizedTable
                    data={rolesData.items}
                    columns={columns}
                    height={500}
                    itemHeight={70}
                    onRowClick={(role) => handleRoleHover(role)}
                  />
                ) : (
                  <DataTable
                    columns={columns}
                    data={rolesData.items}
                    loading={false}
                    error={undefined}
                    onRetry={refetch}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          {/* Mobile card view */}
          <div className="md:hidden space-y-3">
            {rolesData.items.map((role: Role) => (
              <RoleCard 
                key={role.id} 
                role={role}
                onMouseEnter={() => handleRoleHover(role)}
              />
            ))}
          </div>
        </>
      )}
      
      {/* Pagination */}
      {rolesData && rolesData.total > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              {/* Results info */}
              <div className="text-sm text-muted-foreground order-2 sm:order-1">
                {t('pagination.showing')} {((searchParams.page! - 1) * searchParams.size!) + 1} {t('pagination.to')}{' '}
                {Math.min(searchParams.page! * searchParams.size!, rolesData.total)} {t('pagination.of')}{' '}
                {rolesData.total} {t('pagination.entries')}
              </div>
              
              {/* Pagination controls */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto order-1 sm:order-2">
                {/* Page size selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm whitespace-nowrap">{t('pagination.rowsPerPage')}:</span>
                  <Select
                    value={searchParams.size?.toString()}
                    onValueChange={(value) => handlePageSizeChange(Number(value))}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Pagination component */}
                <Pagination
                  currentPage={searchParams.page!}
                  totalPages={rolesData.pages}
                  onPageChange={handlePageChange}
                  showSizeChanger={false}
                  className="justify-center sm:justify-end"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Enhanced Delete Dialog */}
      <RoleDeleteDialog
        role={roleToDelete}
        open={!!roleToDelete}
        onOpenChange={() => setRoleToDelete(null)}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteRoleMutation.isPending}
      />
    </div>
  );
};